import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { OptionDropdown } from '../../../../../shared/components/ui/atoms';
import { useAuth } from '../../../../auth/context/AuthContext';
import { useBorrower } from '../../../../borrower/context/BorrowerContext';

import { useOwnership } from '../../../../onboarding/context/OwnershipContext';
import { ONBOARDING_STEPS } from '../../../../../utils/constants';
import { SpanOption } from '../SpanOption';
import { FooterContentProps, ResumeContentContext, SummaryStep } from '../types';
import { getText } from '../utils/getText';

// Default custom widths for dropdowns
const DEFAULT_DROPDOWN_WIDTHS: Record<number, string> = {
  0: '300px', // state
  4: '300px', // propertyType
  5: '300px', // ficoRange
  6: '300px', // propertiesNegotiated
  1: '300px', // loanType
  3: '300px', // loanOwner
  2: '300px', // propertyType
};

const buildSummaryStep = (node: React.ReactNode): SummaryStep => ({
  element: node,
  isSet: React.isValidElement(node) ? Boolean(node.props?.isSet) : false,
  value: React.isValidElement(node) ? node.props?.isSet : undefined,
});

export const useFooterContext = (props: FooterContentProps): ResumeContentContext | null => {
  const {
    values,
    setValue,
    setMultipleValues,
    setSelectedIndex,
    setSelectedIndexData,
    oneLine,
    hideLastStep,
    isSpan,
    isBrokerMode,
    isBrokerFirst,
    isBrokerLast,
    firstPerson,
    isBrokerResume,
    isProcessingTeamMode,
    borrowerData,
    ownerships,
    investmentAddress,
    forceUnlockCreditInfo = false,
    forceLockCreditInfo = false,
    stateDropdownCustomWidth,
    dropdownCustomWidths,
  } = props;

  const { ownershipSelected } = useOwnership();
  const { ownershipsFiltered: borrowerOwnershipsFiltered } = useBorrower();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const canUnlockCreditInfo = !forceLockCreditInfo;

  const computeInitialLockState = useCallback(() => {
    if (forceLockCreditInfo) {
      return true;
    }

    if (!isLoggedIn) {
      return false;
    }

    return !forceUnlockCreditInfo;
  }, [forceLockCreditInfo, forceUnlockCreditInfo, isLoggedIn]);

  const [isCreditInfoLocked, setIsCreditInfoLocked] = useState<boolean>(computeInitialLockState);

  const handleFocusAddress = useCallback(() => {
    setValue?.('__progress_after_address__', 0);
    setSelectedIndex?.(1);
  }, [setSelectedIndex, setValue]);

  const unlockCreditInfo = useCallback(() => {
    if (!canUnlockCreditInfo) {
      return;
    }

    setIsCreditInfoLocked(false);
  }, [canUnlockCreditInfo]);

  useEffect(() => {
    setIsCreditInfoLocked(computeInitialLockState());
  }, [computeInitialLockState]);

  useEffect(() => {
    if (forceLockCreditInfo) {
      setIsCreditInfoLocked(true);
      return;
    }

    if (forceUnlockCreditInfo) {
      setIsCreditInfoLocked(false);
    }
  }, [forceLockCreditInfo, forceUnlockCreditInfo]);

  useEffect(() => {
    if (borrowerData && ownershipSelected) {
      const ownershipSelectedLastLoanCreditScore = !ownershipSelected?.isNew
        ? ownershipSelected?.loans[ownershipSelected?.loans?.length - 1]?.loanData["What’s your credit score?"]
        : null;
      const ownershipSelectedLastLoanProperties = !ownershipSelected?.isNew
        ? ownershipSelected?.loans[ownershipSelected?.loans?.length - 1]?.loanData["How many properties have you purchased/sold in the last 24 months?"]
        : null;

      const updates: Record<string, any> = {};

      if (ownershipSelectedLastLoanCreditScore) {
        updates[ONBOARDING_STEPS[5].text] = ownershipSelectedLastLoanCreditScore;
      }
      if (ownershipSelectedLastLoanProperties) {
        updates[ONBOARDING_STEPS[6].text] = ownershipSelectedLastLoanProperties;
      }

      if (Object.keys(updates).length > 0) {
        setMultipleValues?.(updates);
      }
    }
  }, [borrowerData, ownershipSelected, setMultipleValues]);

  const normalizedInvestmentAddress = useMemo(() => {
    if (typeof investmentAddress === 'string' && investmentAddress.trim().length > 0) {
      return investmentAddress.trim();
    }

    if (!values) {
      return '';
    }

    const address = (values as any)?.['Investment property address'];
    if (typeof address === 'string' && address.trim().length > 0) {
      // Procesar para mostrar sin país
      return address.replace(/,\s*(USA|United States|US|United States of America)\s*$/i, '').trim();
    }

    return '';
  }, [investmentAddress, values]);

  const shouldUseInvestmentAddress = isLoggedIn && normalizedInvestmentAddress.length > 0;

  const resolvedOwnerships = useMemo(() => {
    if (Array.isArray(ownerships) && ownerships.length > 0) {
      return ownerships;
    }
    if (Array.isArray(borrowerOwnershipsFiltered) && borrowerOwnershipsFiltered.length > 0) {
      return borrowerOwnershipsFiltered;
    }
    return [];
  }, [ownerships, borrowerOwnershipsFiltered]);

  const Wrapper = useMemo(() => {
    if (isSpan) {
      const SpanWrapper = (wrapperProps: { isSet: any; index: number }) => {
        const stepOptions = ONBOARDING_STEPS[wrapperProps.index]?.options || [];
        // Buscar la opción que coincida con el valor, comparando tanto text como el valor directamente
        const selectedOption = stepOptions.find((op: any) => {
          return op.text === wrapperProps.isSet || 
                 (typeof wrapperProps.isSet === 'string' && op.text.toLowerCase() === wrapperProps.isSet.toLowerCase());
        });
        const isSpecial = selectedOption && 'isSpecial' in selectedOption && selectedOption.isSpecial === true;
        return (
          <SpanOption 
            text={getText(values, wrapperProps.isSet, wrapperProps.index) || wrapperProps.isSet || ''} 
            isSpecial={isSpecial}
          />
        );
      };
      SpanWrapper.displayName = 'SpanWrapper';
      return SpanWrapper;
    }

    const DefaultWrapper = (wrapperProps: any) => {
      // For brokers and "Someone else" guarantors, always allow editing FICO and properties (indices 5 and 6)
      const isFicoOrPropertiesField = wrapperProps.index === 5 || wrapperProps.index === 6;
      const isLockedField =
        isLoggedIn &&
        isCreditInfoLocked &&
        isFicoOrPropertiesField &&
        !isProcessingTeamMode &&
        !isBrokerMode &&
        !isSomeoneElse;
      const mergedDisabled = wrapperProps.isDisabled ?? isLockedField;
      
      // Determine customWidth: priority: dropdownCustomWidths[index] > stateDropdownCustomWidth (for index 0) > DEFAULT_DROPDOWN_WIDTHS[index] > wrapperProps.customWidth
      // Use nullish coalescing to ensure we use default values correctly
      let customWidth = 
        dropdownCustomWidths?.[wrapperProps.index] ?? 
        (wrapperProps.index === 0 ? stateDropdownCustomWidth : undefined);
      
      // If no custom width was provided, use default if available
      if (customWidth === undefined || customWidth === null) {
        customWidth = DEFAULT_DROPDOWN_WIDTHS[wrapperProps.index] ?? wrapperProps.customWidth;
      }

      return (
        <OptionDropdown
          {...wrapperProps}
          isBrokerMode={isBrokerMode}
          values={values}
          setValue={setValue}
          setSelectedIndex={setSelectedIndex}
          isDisabled={mergedDisabled}
          customWidth={customWidth}
        />
      );
    };
    DefaultWrapper.displayName = 'DefaultWrapper';
    return DefaultWrapper;
  }, [isSpan, values, isCreditInfoLocked, isBrokerMode, isLoggedIn, isProcessingTeamMode, setValue, setSelectedIndex, stateDropdownCustomWidth, dropdownCustomWidths]);

  if (!values) {
    return null;
  }

  let state = (
    <Wrapper
      isSet={values[ONBOARDING_STEPS[0].text]}
      index={0}
    />
  );

  // For brokers in resume processing, always show state dropdown instead of address
  if (shouldUseInvestmentAddress && !isBrokerMode) {
    if (isSpan) {
      state = (
        <SpanOption
          text={normalizedInvestmentAddress}
          isSet={normalizedInvestmentAddress}
          onClick={handleFocusAddress}
          role='button'
          tabIndex={0}
          disableUnderline
          onKeyDown={(event: React.KeyboardEvent<HTMLSpanElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleFocusAddress();
            }
          }}
        />
      );
    } else {
      state = React.cloneElement(
        <span className='option'>{normalizedInvestmentAddress}</span>,
        {
          isSet: normalizedInvestmentAddress,
          onClick: handleFocusAddress,
          role: 'button',
          tabIndex: 0,
          className: 'option no-underline',
          onKeyDown: (event: React.KeyboardEvent<HTMLSpanElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleFocusAddress();
            }
          },
        },
      );
    }
  }

  const guarantorText = (() => {
    if (ownershipSelected) {
      if (ownershipSelected.isNew) {
        const first = (values as any)?.guarantorFirstName || '';
        const last = (values as any)?.guarantorLastName || '';
        const full = `${first} ${last}`.trim();
        return full || 'someone else';
      }

      const name = `${ownershipSelected.name || ''} ${ownershipSelected.lastname || ''}`.trim();
      return name || 'Guarantor';
    }

    const first = (values as any)?.guarantorFirstName || '';
    const last = (values as any)?.guarantorLastName || '';
    const full = `${first} ${last}`.trim();
    return full || '';
  })();

  const guarantorElement = (
    <SpanOption
      text={guarantorText}
      isSet={guarantorText}
    />
  );

  const isSomeoneElse = ownershipSelected?.isNew === true;

  return {
    steps: {
      state: buildSummaryStep(state),
      loanType: buildSummaryStep(
        <Wrapper
          isSet={values[ONBOARDING_STEPS[1].text]}
          index={1}
        />,
      ),
      loanOwner: buildSummaryStep(
        <Wrapper
          isSet={values[ONBOARDING_STEPS[3].text]}
          index={3}
        />,
      ),
      propertyType: buildSummaryStep(
        <Wrapper
          isSet={values[ONBOARDING_STEPS[4].text]}
          index={4}
        />,
      ),
      ficoRange: buildSummaryStep(
        (() => {
          const ficoValue = values[ONBOARDING_STEPS[5].text];
          return (
            <Wrapper
              isSet={ficoValue}
              index={5}
            />
          );
        })(),
      ),
      propertiesNegotiated: buildSummaryStep(
        <Wrapper
          isSet={values[ONBOARDING_STEPS[6].text]}
          index={6}
        />,
      ),
    },
    guarantor: {
      element: guarantorElement,
      text: guarantorText,
      isPresent: Boolean(guarantorText),
    },
    flags: {
      isBrokerMode,
      oneLine,
      hideLastStep,
      firstPerson,
      isBrokerResume,
      isProcessingTeamMode,
      isBrokerFirst,
      isBrokerLast,
      isLoggedIn,
    },
    borrowerData,
    ownerships: resolvedOwnerships,
    values,
    isCreditInfoLocked,
    unlockCreditInfo,
    ownershipSelected,
    canUnlockCreditInfo,
    // For brokers, always use "in" (state) instead of "at" (address)
    hasInvestmentAddress: isBrokerMode ? false : shouldUseInvestmentAddress,
    isSomeoneElse,
    setSelectedIndex,
  };
};

